// Nouveau fichier principal : RentalPropertyAnalyzer.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PropertyForm from './components/PropertyForm';
import PropertyReport from './components/PropertyReport';
import HomeScreen from './components/HomeScreen';
import AmortizationPage from './components/AmortizationPage';
import BuildingDashboard from './components/BuildingDashboard';
import ProjectDocumentsPage from './components/projectDocuments/ProjectDocumentsPage';
import FinancementDossierPage from './components/financementDossier/FinancementDossierPage';
import FinancingScenarioForm from './components/FinancingScenarioForm';
import DealPurposeModal from './components/DealPurposeModal';
import ListingImportModal from './components/ListingImportModal';
import AboutPage from './components/AboutPage';
import useRentabilityCalculator from './hooks/useRentabilityCalculator';
import defaultProperty from './defaults/defaultProperty';
import { applyListingImport } from './utils/applyListingImport';
import { propertyHasGalleryImages } from './utils/propertyImages';
import { isOwnedDeal, FINANCING_TYPE_EXISTING } from './utils/dealPurpose';
import { useAuth } from './contexts/AuthContext';
import { useLanguage } from './contexts/LanguageContext';
import {
  saveProperty,
  updateProperty,
  deletePropertyAndScenarios,
  duplicateProperty,
  importSharedProperty,
  propertyHasActiveBrokerDashboardShare,
  getActiveBrokerDashboardShareUids,
  saveFinancingScenario,
  getScenariosOnce,
  updateScenario,
} from './services/dataService';
import { deleteFinancementDossiersByProperty } from './services/financementDossierService';
import { deletePrivateLenderOffersByProperty } from './services/privateLenderOfferService';
import { deleteGeneralDossier } from './services/generalDossierService';
import DeletePropertyConfirmModal from './components/DeletePropertyConfirmModal';
import DuplicatePropertyModal from './components/DuplicatePropertyModal';
import Header from './components/Header';
import FloatingDirectChatDock from './components/directChat/FloatingDirectChatDock';
import FloatingFeedbackDock from './components/feedback/FloatingFeedbackDock';
import OnboardingModal from './components/onboarding/OnboardingModal';
import { OnboardingProvider, FloatingOnboardingAssistant } from './onboarding';
import ClientsPage from './components/clients/ClientsPage';
import ChecklistsPage from './components/checklists/ChecklistsPage';
import MarketParamsPage from './components/marketParams/MarketParamsPage';
import MyFilesPage from './components/myFiles/MyFilesPage';
import ShareModal from './components/share/ShareModal';
import ShareWithBrokerModal from './components/share/ShareWithBrokerModal';
import ShareMethodChoiceModal from './components/share/ShareMethodChoiceModal';
import BrokerPickerModal from './components/share/BrokerPickerModal';
import SharedPropertyView from './components/shared/SharedPropertyView';
import {
  getShare,
  markSharedWithMeSeen,
  removeSharedWithMe,
  getSharesByProperty,
  getShareScenariosOnce,
  cleanupPropertyShares,
  getPropertyShareRecipientCount,
  refreshLiveShareSnapshots,
} from './services/shareService';
import { getClients, getInviteByToken, updateClient } from './services/clientsService';
import { setUserProfile } from './services/userProfileService';
import { addBrokerLink } from './services/brokerLinksService';
import { isBrokerPersona } from './constants/personas';
import {
  canAcceptInvitationWithCurrentUser,
  formatInvitationAcceptError,
} from './utils/invitationEmail';

const RentalPropertyAnalyzer = () => {
  const { currentUser, userProfile, properties, propertiesLoading, sharedWithMe, linkedBrokers, shareEligibleBrokerLinks, financedDealsAsOwner, financedDealsAsLender, fundInvestorsAsInvestor, refreshSharedWithMe, refreshUserProfile } = useAuth();
  const { t, language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState('home');
  const [propertyToDelete, setPropertyToDelete] = useState(null);
  const [propertyToDuplicate, setPropertyToDuplicate] = useState(null);
  const [pendingLoanDetailDeal, setPendingLoanDetailDeal] = useState(null);
  const [postInviteFocus, setPostInviteFocus] = useState(null);

  useEffect(() => {
    if (location.state?.shareAdded) {
      setShowShareAddedBanner(true);
      setShareStatsVersion((v) => v + 1);
      navigate('/', { replace: true, state: {} });
      const t = setTimeout(() => setShowShareAddedBanner(false), 5000);
      return () => clearTimeout(t);
    }
  }, [location.state?.shareAdded, navigate]);

  useEffect(() => {
    if (location.state?.pocketDealLinkEmailMismatch) {
      setShowPocketDealLinkEmailMismatchBanner(true);
      navigate('/', { replace: true, state: {} });
    }
  }, [location.state?.pocketDealLinkEmailMismatch, navigate]);

  useEffect(() => {
    if (location.state?.invitationAccepted) {
      setInvitationAcceptedMessage(true);
      // Preserve focus instructions before state is cleared by replace navigation.
      const focusTab = location.state?.focusTab;
      const openFundInvestorId = location.state?.openFundInvestorId;
      if (focusTab || openFundInvestorId) {
        setPostInviteFocus({
          focusTab: typeof focusTab === 'string' ? focusTab : null,
          openFundInvestorId: typeof openFundInvestorId === 'string' ? openFundInvestorId : null,
        });
      }
      navigate('/', { replace: true, state: {} });
    }
  }, [location.state?.invitationAccepted, location.state?.focusTab, location.state?.openFundInvestorId, navigate]);

  // Clear focus after first render pass (prevents reopening on future renders).
  useEffect(() => {
    if (!postInviteFocus) return undefined;
    const tmr = setTimeout(() => setPostInviteFocus(null), 800);
    return () => clearTimeout(tmr);
  }, [postInviteFocus]);
  const [currentProperty, setCurrentProperty] = useState(defaultProperty);
  const [advancedExpenses, setAdvancedExpenses] = useState(false);
  const [currentScenario, setCurrentScenario] = useState(null);
  const [amortizationData, setAmortizationData] = useState(null);
  const [clients, setClients] = useState([]);
  const [shareModalPropertyId, setShareModalPropertyId] = useState(null);
  const [shareModalInitialTab, setShareModalInitialTab] = useState('email');
  const [shareModalVipDraft, setShareModalVipDraft] = useState(null);
  /** Renvoi depuis Campagnes pocket : masquer onglets courriel / lien, afficher seulement VIP. */
  const [shareModalOnlyVipTab, setShareModalOnlyVipTab] = useState(false);
  const [shareWithBrokerPropertyId, setShareWithBrokerPropertyId] = useState(null);
  const [selectedBrokerForShare, setSelectedBrokerForShare] = useState(null);
  const [shareBrokerPickerPropertyId, setShareBrokerPickerPropertyId] = useState(null);
  const [shareMethodChoicePropertyId, setShareMethodChoicePropertyId] = useState(null);
  const [shareStatsVersion, setShareStatsVersion] = useState(0);
  const [directChatDockOpen, setDirectChatDockOpen] = useState(false);
  const [feedbackDockOpen, setFeedbackDockOpen] = useState(false);
  const [shareStats, setShareStats] = useState({});
  const [invitationAcceptedMessage, setInvitationAcceptedMessage] = useState(null);
  const [invitationLinkError, setInvitationLinkError] = useState(null);
  const [showShareAddedBanner, setShowShareAddedBanner] = useState(false);
  const [showPocketDealLinkEmailMismatchBanner, setShowPocketDealLinkEmailMismatchBanner] = useState(false);
  const [viewingShareData, setViewingShareData] = useState(null);
  const [sharedScenariosFromClients, setSharedScenariosFromClients] = useState([]);
  const [selectedFinancementDossier, setSelectedFinancementDossier] = useState(null);
  const feedbackEnabled = import.meta.env.VITE_FEEDBACK_ENABLED !== 'false';
  const [lockedFields] = useState({
    //maintenance: false,
    //concierge: false,
    debtCoverage: true,
    welcomeTax: true
  });

  const analysis = useRentabilityCalculator(
    currentProperty,
    advancedExpenses,
    lockedFields,
    setCurrentProperty,
  );

  const handleOpenDealFromChat = useCallback(
    (payload) => {
      if (payload.type === 'shared' && payload.shareToken) {
        navigate({ pathname: '/', search: `?share=${encodeURIComponent(payload.shareToken)}` });
        setDirectChatDockOpen(false);
        setViewingShareData(null);
        return;
      }
      if (payload.type === 'owned' && payload.propertyId) {
        const prop = properties?.find((p) => p.id === payload.propertyId);
        if (prop) {
          setCurrentProperty(prop);
          setAdvancedExpenses(!!prop.advancedExpenses);
          setCurrentStep('dashboard');
        }
        // Garder le panneau messages ouvert (fil actif) pendant la navigation vers le tableau de bord.
        setViewingShareData(null);
      }
    },
    [navigate, properties],
  );

  useEffect(() => {
    if (isBrokerPersona(userProfile?.persona) && currentUser?.uid) {
      const unsub = getClients(currentUser.uid, setClients);
      return () => {
        const u = unsub;
        queueMicrotask(() => u?.());
      };
    }
    setClients([]);
    return undefined;
  }, [userProfile?.persona, currentUser?.uid]);

  // Migration : si le profil a encore brokerUid/brokerClientId mais brokerLinks est vide, ajouter le lien
  useEffect(() => {
    if (!currentUser?.uid || !userProfile?.brokerUid || !userProfile?.brokerClientId) return;
    if (linkedBrokers.length > 0) return;
    addBrokerLink(
      currentUser.uid,
      userProfile.brokerUid,
      userProfile.brokerClientId,
      userProfile.brokerDisplayName || ''
    ).catch((e) => console.warn('brokerLinks migration failed', e?.message));
  }, [currentUser?.uid, userProfile?.brokerUid, userProfile?.brokerClientId, userProfile?.brokerDisplayName, linkedBrokers.length]);

  useEffect(() => {
    const importShared = async () => {
      const params = new URLSearchParams(window.location.search);
      const shared = params.get('share');
      if (!shared || !currentUser) return;
      if (shared.length === 32 && /^[0-9a-fA-F]+$/.test(shared)) return;
      try {
        const data = JSON.parse(decodeURIComponent(atob(shared)));
        await importSharedProperty(data, currentUser.uid);
        alert(t('property.import.success'));
      } catch (e) {
        console.error('Import error', e);
      } finally {
        params.delete('share');
        const newUrl = `${window.location.pathname}${
          params.toString() ? `?${params.toString()}` : ''
        }`;
        window.history.replaceState({}, '', newUrl);
      }
    };
    importShared();
  }, [currentUser, t]);

  // When an existing user opens ?invitation= on home: borrower (client) link here; capital investor → /signup
  useEffect(() => {
    if (!currentUser || !refreshUserProfile) return;
    const paramsAtStart = new URLSearchParams(location.search);
    const invitationToken = paramsAtStart.get('invitation');
    if (!invitationToken) return;
    let cancelled = false;

    const stripInvitationFromUrl = () => {
      const p = new URLSearchParams(paramsAtStart.toString());
      p.delete('invitation');
      const qs = p.toString();
      navigate(qs ? `/?${qs}` : '/', { replace: true });
    };

    getInviteByToken(invitationToken)
      .then(async (inviteData) => {
        if (cancelled || !inviteData) return;
        if (inviteData.fundInvestorId && !inviteData.clientId) {
          navigate(`/signup?invitation=${encodeURIComponent(invitationToken)}`, { replace: true });
          return;
        }
        if (!inviteData.clientId) {
          stripInvitationFromUrl();
          return;
        }
        if (!canAcceptInvitationWithCurrentUser(inviteData.email, currentUser.email)) {
          const invited = String(inviteData.email || '').trim() || '—';
          setInvitationLinkError(String(t('auth.invitationWrongAccount')).replace(/\{email\}/g, invited));
          stripInvitationFromUrl();
          return;
        }
        try {
          await updateClient(inviteData.clientId, { clientUserId: currentUser.uid });
          await setUserProfile(currentUser.uid, {
            persona: 'investisseur',
            onboardingCompleted: true,
            brokerClientId: inviteData.clientId,
            brokerUid: inviteData.brokerUid,
          });
          await addBrokerLink(
            currentUser.uid,
            inviteData.brokerUid,
            inviteData.clientId,
            inviteData.brokerDisplayName || '',
          );
          await refreshUserProfile();
          if (cancelled) return;
          setInvitationAcceptedMessage(true);
        } catch (e) {
          if (!cancelled) {
            setInvitationLinkError(formatInvitationAcceptError(e, t, inviteData?.email));
          }
        }
        if (!cancelled) stripInvitationFromUrl();
      })
      .catch((e) => {
        if (cancelled) return;
        setInvitationLinkError(formatInvitationAcceptError(e, t, null));
        stripInvitationFromUrl();
      });
    return () => { cancelled = true; };
  }, [currentUser, location.search, navigate, refreshUserProfile, t]);

  const handleShare = async (propertyId) => {
    if (isBrokerPersona(userProfile?.persona)) {
      const property = properties?.find((p) => p.id === propertyId);
      if (property?.fromClient) return; // Ne pas permettre au courtier de partager un immeuble partagé par un client (données confidentielles)
      setShareModalVipDraft(null);
      setShareModalOnlyVipTab(false);
      setShareModalInitialTab('email');
      setShareModalPropertyId(propertyId);
      return;
    }
    // Investisseur avec au moins un lien emprunteur valide (clients/{id} + clientUserId) : courtier / prêteur privé.
    if (shareEligibleBrokerLinks?.length > 0) {
      setShareMethodChoicePropertyId(propertyId);
      return;
    }
    // Sinon partager par courriel (même options : Lecture, Lecture/Écriture, sous-scénarios)
    setShareModalVipDraft(null);
    setShareModalOnlyVipTab(false);
    setShareModalInitialTab('email');
    setShareModalPropertyId(propertyId);
  };

  const handleResubmitPocketCampaign = useCallback((campaign) => {
    if (!campaign?.propertyId) return;
    setShareModalOnlyVipTab(true);
    setShareModalPropertyId(campaign.propertyId);
    setShareModalVipDraft({
      subjectLine: campaign.subjectLine ?? '',
      preheader: campaign.preheader ?? '',
      introText: campaign.introText ?? '',
      includeDescription: campaign.includeDescription !== undefined ? !!campaign.includeDescription : true,
      includeImage: campaign.includeImage !== undefined ? !!campaign.includeImage : true,
      includeMetrics: campaign.includeMetrics !== undefined ? !!campaign.includeMetrics : true,
      includeScenario: !!campaign.includeScenario,
      scenarioId: campaign.scenarioId || '',
      locale: campaign.locale || 'fr',
      emailLanguages:
        campaign.emailLanguages === 'fr' || campaign.emailLanguages === 'en' || campaign.emailLanguages === 'both'
          ? campaign.emailLanguages
          : campaign.locale === 'en'
            ? 'en'
            : 'fr',
    });
    setShareModalInitialTab('vip');
  }, []);

  const [marketingSectionScrollTrigger, setMarketingSectionScrollTrigger] = useState(0);
  const [dealPurposeModalOpen, setDealPurposeModalOpen] = useState(false);
  const [listingImportOpen, setListingImportOpen] = useState(false);
  const [listingImportWarnings, setListingImportWarnings] = useState([]);
  const [pendingListingDeal, setPendingListingDeal] = useState(null);

  const handleSave = async () => {
    const isFirstSave = !currentProperty.id;
    const fieldsToSave = [
      'address',
      'city',
      'clientId',
      'province',
      'postalCode',
      'askingPrice',
      'purchasePrice',
      'municipalEvaluation',
      'numberOfUnits',
      'annualRent',
      'parkingRevenue',
      'internetRevenue',
      'storageRevenue',
      'otherRevenue',
      'vacancyRate',
      'insurance',
      'municipalTaxes',
      'schoolTaxes',
      'electricityHeating',
      'heating',
      'electricity',
      'maintenance',
      'managementRate',
      'concierge',
      'landscaping',
      'snowRemoval',
      'extermination',
      'fireInspection',
      'advertising',
      'legal',
      'accounting',
      'cableInternet',
      'appliances',
      'garbage',
      'washerDryer',
      'hotWater',
      'operatingExpenses',
      'otherExpenses',
      'structureType',
      'schlBenchmarkVersion',
      'dealPurpose',
      'financingType',
      'assumedLoanBalance',
      'assumptionStatedMonthlyPayment',
      'assumptionTermEnd',
      'mortgageRate',
      'amortization',
      'term',
      'numFridges',
      'numStoves',
      'numDishwashers',
      'numWashers',
      'numDryers',
      'numHeatPumps',
      'numElevators',
      'cmhcAnalysis',
      'cmhcTax',
      'welcomeTax',
      'buildingImageUrl',
      'buildingImageUrls',
      'marketingDescriptionHtml',
      /** Courtier immo (module offres) : mandat de vente sur l’analyse — requis pour le filtre d’accueil et ShareModal. */
      'brokerSaleMandate',
      'rentRegister',
    ];
    const baseProperty = { structureType: 'woodFrame', ...currentProperty };
    const propertyData = fieldsToSave.reduce((acc, key) => {
      const value = baseProperty[key];
      if (value !== undefined) acc[key] = value;
      return acc;
    }, {});
    // Ne pas envoyer buildingImageUrls / buildingImageUrl vides : évite d’effacer la galerie
    // si l’état local était désynchronisé alors que Firestore contient encore les URLs.
    if (!propertyHasGalleryImages(propertyData)) {
      delete propertyData.buildingImageUrls;
      delete propertyData.buildingImageUrl;
    }
    // Ne pas écraser uid quand le courtier sauvegarde un immeuble client : garder le propriétaire (client).
    const isClientProperty = currentProperty.fromClient || currentProperty.uid !== currentUser?.uid;
    const ownerUid = isClientProperty && currentProperty.uid ? currentProperty.uid : currentUser.uid;
    const propertyWithAnalysis = {
      ...propertyData,
      advancedExpenses,
      ...analysis,
      uid: ownerUid,
      ...((userProfile?.brokerClientId || shareEligibleBrokerLinks?.[0]?.clientId) && !isClientProperty && {
        brokerUid: userProfile?.brokerUid || shareEligibleBrokerLinks?.[0]?.brokerUid,
        brokerClientId: userProfile?.brokerClientId || shareEligibleBrokerLinks?.[0]?.clientId,
      }),
      ...(currentProperty.fromClient && currentProperty.brokerUid && {
        brokerUid: currentProperty.brokerUid,
        clientId: currentProperty.clientId,
      }),
    };
    const cleanProperty = Object.fromEntries(
      Object.entries(propertyWithAnalysis).filter(([, v]) => v !== undefined)
    );
    let savedPropertyId = currentProperty.id;
    if (currentProperty.id) {
      await updateProperty(currentProperty.id, cleanProperty);
      setCurrentProperty({ ...currentProperty, ...cleanProperty });
    } else {
      const newId = await saveProperty(cleanProperty);
      savedPropertyId = newId;
      setCurrentProperty({ ...cleanProperty, id: newId });
    }

    if (isOwnedDeal(cleanProperty) && savedPropertyId) {
      const financing = {
        financingType: cleanProperty.financingType || FINANCING_TYPE_EXISTING,
        assumedLoanBalance: cleanProperty.assumedLoanBalance || '',
        assumptionStatedMonthlyPayment: cleanProperty.assumptionStatedMonthlyPayment || '',
        assumptionTermEnd: cleanProperty.assumptionTermEnd || '',
        mortgageRate: cleanProperty.mortgageRate || '5.5',
      };

      if (isFirstSave) {
        const scenarioId = await saveFinancingScenario(
          savedPropertyId,
          {
            title: t('ownedFinancing.currentSituationTitle'),
            financing,
            acquisitionCosts: {},
          },
          currentUser?.uid,
        );
        setCurrentScenario({
          id: scenarioId,
          title: t('ownedFinancing.currentSituationTitle'),
          type: 'initialFinancing',
          financing,
          acquisitionCosts: {},
          createdByUid: currentUser?.uid,
        });
        setCurrentStep('report');
        return;
      }

      // Keep "Situation actuelle" in sync when the owned form financing changes
      const scenarios = await getScenariosOnce(savedPropertyId);
      const currentSituation = scenarios.find((s) => s.type === 'initialFinancing');
      if (currentSituation?.id) {
        await updateScenario(savedPropertyId, currentSituation.id, { financing });
        if (currentScenario?.id === currentSituation.id) {
          setCurrentScenario({ ...currentScenario, financing });
        }
      }
      try {
        await refreshLiveShareSnapshots(savedPropertyId, ownerUid);
      } catch (e) {
        console.warn('refreshLiveShareSnapshots', e);
      }
    }

    if (isFirstSave) {
      setMarketingSectionScrollTrigger((n) => n + 1);
    } else {
      setCurrentStep('dashboard');
    }
  };

  const handleViewAmortization = (
    property,
    analysisData,
    scenarioData,
    scenarioAnalysis,
  ) => {
    setAmortizationData({
      property,
      analysis: analysisData,
      scenario: scenarioData,
      scenarioAnalysis,
    });
    setCurrentStep('amortization');
  };

  const resetProperty = () => {
    setDealPurposeModalOpen(true);
  };

  const handleDealPurposeSelect = ({ dealPurpose, financingType, importListing }) => {
    setDealPurposeModalOpen(false);
    if (importListing) {
      setPendingListingDeal({ dealPurpose, financingType });
      setListingImportOpen(true);
      return;
    }
    setListingImportWarnings([]);
    setCurrentProperty({
      ...defaultProperty,
      dealPurpose,
      financingType,
    });
    setAdvancedExpenses(false);
    setCurrentScenario(null);
    setCurrentStep('form');
  };

  const handleListingImportApply = ({ fields, warnings, advancedExpenses: useAdvanced }) => {
    const base = {
      ...defaultProperty,
      dealPurpose: pendingListingDeal?.dealPurpose || currentProperty?.dealPurpose || 'acquisition',
      financingType: pendingListingDeal?.financingType || currentProperty?.financingType || 'conventional',
    };
    const merged = applyListingImport(
      currentStep === 'form' && currentProperty ? { ...base, ...currentProperty } : base,
      fields
    );
    setCurrentProperty(merged);
    setAdvancedExpenses(!!useAdvanced);
    setListingImportWarnings(Array.isArray(warnings) ? warnings : []);
    setListingImportOpen(false);
    setPendingListingDeal(null);
    setCurrentScenario(null);
    setCurrentStep('form');
  };

  const handleListingImportClose = () => {
    setListingImportOpen(false);
    setPendingListingDeal(null);
  };

  const isCourtierHypo = isBrokerPersona(userProfile?.persona);

  const handleDeleteProperty = async (property) => {
    const propertyId = property?.id ?? property;
    const ownerUid = typeof property === 'object' && property?.uid != null ? property.uid : currentUser?.uid;
    if (!propertyId || !ownerUid) return;
    try {
      await cleanupPropertyShares(ownerUid, propertyId);
      await deleteFinancementDossiersByProperty(propertyId, ownerUid);
      await deletePrivateLenderOffersByProperty(propertyId, ownerUid);
      await deleteGeneralDossier(propertyId);
      await deletePropertyAndScenarios(propertyId);
      setPropertyToDelete(null);
    } catch (e) {
      console.error(e);
      alert((t('share.error') || 'Erreur') + ': ' + (e?.message || e));
    }
  };

  const handleDuplicateProperty = async (includeScenarios) => {
    const property = propertyToDuplicate;
    if (!property?.id || !currentUser?.uid) return;
    try {
      await duplicateProperty(property.id, currentUser.uid, { includeScenarios });
      setPropertyToDuplicate(null);
    } catch (e) {
      console.error(e);
      alert((t('share.error') || 'Erreur') + ': ' + (e?.message || e));
    }
  };

  // When user views home, refresh "Partagés avec moi" for all personas so entries whose share was deleted disappear
  useEffect(() => {
    if (currentStep === 'home' && currentUser?.uid && refreshSharedWithMe) {
      refreshSharedWithMe();
    }
  }, [currentStep, currentUser?.uid, refreshSharedWithMe]);

  // Share stats for home screen (lifted here so state updates reliably trigger re-render of HomeScreen)
  const propertyIdsKey = (properties || []).map((p) => p.id).filter(Boolean).join(',');
  useEffect(() => {
    if (!currentUser?.uid || !properties?.length) {
      setShareStats({});
      return;
    }
    const ownedIds = properties.filter((p) => p.uid === currentUser.uid).map((p) => p.id).filter(Boolean);
    if (!ownedIds.length) {
      setShareStats({});
      return;
    }
    let cancelled = false;
    const load = async () => {
      const nextCount = {};
      await Promise.all(
        ownedIds.map(async (id) => {
          if (cancelled) return;
          const count = await getPropertyShareRecipientCount(currentUser.uid, id).catch(() => 0);
          if (!cancelled) {
            nextCount[id] = count;
          }
        })
      );
      if (!cancelled) {
        setShareStats(nextCount);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [currentUser?.uid, propertyIdsKey, shareStatsVersion, currentStep]);

  // Refetch share stats when user returns to the app tab (e.g. recipient removed share in another tab)
  useEffect(() => {
    const onFocus = () => setShareStatsVersion((v) => v + 1);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  useEffect(() => {
    if (
      currentStep !== 'dashboard' ||
      !currentProperty?.id ||
      !currentProperty?.fromClient ||
      currentProperty?.brokerUid !== currentUser?.uid ||
      currentProperty?.brokerSeenAt
    ) return;
    const markSeen = async () => {
      try {
        await updateProperty(currentProperty.id, { brokerSeenAt: new Date() });
        setCurrentProperty((prev) => ({ ...prev, brokerSeenAt: new Date() }));
      } catch (e) {
        console.error(e);
      }
    };
    markSeen();
  }, [currentStep, currentProperty?.id, currentProperty?.fromClient, currentProperty?.brokerUid, currentProperty?.brokerSeenAt, currentUser?.uid]);

  // Charger les sous-scénarios créés par les clients dans les partages (côté courtier propriétaire).
  // Garder la liste en dashboard ET en report pour afficher la section dans le Profitability Analysis Report.
  useEffect(() => {
    const onDashboardOrReport = currentStep === 'dashboard' || currentStep === 'report';
    if (
      !onDashboardOrReport ||
      !currentProperty?.id ||
      !currentUser?.uid ||
      currentProperty?.uid !== currentUser?.uid
    ) {
      setSharedScenariosFromClients([]);
      return;
    }
    let cancelled = false;
    getSharesByProperty(currentUser.uid, currentProperty.id)
      .then((shares) => {
        if (cancelled) return;
        return Promise.all(
          shares.map((s) =>
            getShareScenariosOnce(s.id).then((scenarios) =>
              scenarios.map((sc) => ({ ...sc, shareToken: s.id }))
            )
          )
        );
      })
      .then((arrays) => {
        if (!cancelled && arrays) setSharedScenariosFromClients(arrays.flat());
      })
      .catch((e) => {
        if (!cancelled) {
          console.warn('Load shared scenarios from clients:', e);
          setSharedScenariosFromClients([]);
        }
      });
    return () => { cancelled = true; };
  }, [currentStep, currentProperty?.id, currentProperty?.uid, currentUser?.uid]);

  /** Ferme le deal partagé (marque comme vu si entrée « Partagés avec moi »). À appeler aussi depuis le menu du header. */
  const exitSharedDealViewMarkSeen = useCallback(() => {
    setViewingShareData((vd) => {
      if (vd?.sharedDocId && currentUser?.uid) {
        markSharedWithMeSeen(currentUser.uid, vd.sharedDocId).catch((e) => console.error(e));
      }
      return null;
    });
  }, [currentUser?.uid]);

  const removeSharedPropertyFromDashboard = useCallback(async () => {
    const vd = viewingShareData;
    if (!vd?.sharedDocId || !currentUser?.uid || !vd?.shareData?.id) return;
    try {
      await removeSharedWithMe(currentUser.uid, vd.sharedDocId, vd.shareData.id);
    } catch (e) {
      console.error(e);
    }
    setViewingShareData(null);
  }, [viewingShareData, currentUser?.uid]);

  return (
    <OnboardingProvider
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
      setCurrentProperty={setCurrentProperty}
      currentProperty={currentProperty}
      currentScenario={currentScenario}
      disabled={Boolean(viewingShareData)}
    >
    <div className="min-h-screen bg-gray-50 rentalyzer-main-with-docks overflow-x-hidden">
      <OnboardingModal />
      {invitationAcceptedMessage && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 max-w-md px-4 py-3 bg-green-100 border border-green-300 rounded-lg text-green-800 shadow flex items-center justify-between gap-4">
          <p className="font-medium">{t('shareWithBroker.invitationLinked')}</p>
          <button
            type="button"
            onClick={() => setInvitationAcceptedMessage(null)}
            className="text-green-600 hover:text-green-800 text-xl leading-none"
            aria-label={t('cancel')}
          >
            ×
          </button>
        </div>
      )}
      {invitationLinkError && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 max-w-lg px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-900 shadow flex items-start justify-between gap-4">
          <p className="text-sm font-medium pr-2">{invitationLinkError}</p>
          <button
            type="button"
            onClick={() => setInvitationLinkError(null)}
            className="text-red-600 hover:text-red-800 text-xl leading-none shrink-0"
            aria-label={t('cancel')}
          >
            ×
          </button>
        </div>
      )}
      {!viewingShareData && (
        <>
      {shareMethodChoicePropertyId && (
        <ShareMethodChoiceModal
          propertyId={shareMethodChoicePropertyId}
          onClose={() => setShareMethodChoicePropertyId(null)}
          onChooseBroker={(id) => {
            setShareMethodChoicePropertyId(null);
            if (shareEligibleBrokerLinks.length === 1) {
              setSelectedBrokerForShare(shareEligibleBrokerLinks[0]);
              setShareWithBrokerPropertyId(id);
            } else {
              setShareBrokerPickerPropertyId(id);
            }
          }}
          onChooseEmail={(id) => {
            setShareMethodChoicePropertyId(null);
            setShareModalVipDraft(null);
            setShareModalOnlyVipTab(false);
            setShareModalInitialTab('email');
            setShareModalPropertyId(id);
          }}
        />
      )}
      {shareBrokerPickerPropertyId && shareEligibleBrokerLinks.length > 1 && (
        <BrokerPickerModal
          brokers={shareEligibleBrokerLinks}
          alreadySharedBrokerUids={(() => {
            const prop = properties?.find((p) => p.id === shareBrokerPickerPropertyId);
            return prop ? getActiveBrokerDashboardShareUids(prop) : [];
          })()}
          onClose={() => setShareBrokerPickerPropertyId(null)}
          onSelect={(broker) => {
            setSelectedBrokerForShare(broker);
            setShareWithBrokerPropertyId(shareBrokerPickerPropertyId);
            setShareBrokerPickerPropertyId(null);
          }}
        />
      )}
      {shareModalPropertyId && (
        <ShareModal
          propertyId={shareModalPropertyId}
          propertyBrokerSaleMandate={
            !!(properties?.find((x) => x.id === shareModalPropertyId)?.brokerSaleMandate)
          }
          purchaseOffersModuleEnabled={userProfile?.modules?.purchaseOffersEnabled === true}
          isOwnedDeal={isOwnedDeal(properties?.find((x) => x.id === shareModalPropertyId))}
          showPocketVip={userProfile?.persona === 'courtier_immo'}
          showBrokerShareOptions={(() => {
            if (isBrokerPersona(userProfile?.persona)) return true;
            const p = properties?.find((x) => x.id === shareModalPropertyId);
            return !!(
              currentUser?.uid &&
              p &&
              p.uid === currentUser.uid &&
              !p.fromClient
            );
          })()}
          shareRecordedAsBroker={isBrokerPersona(userProfile?.persona)}
          initialShareTab={shareModalInitialTab}
          vipDraft={shareModalVipDraft}
          onlyVipShareTab={shareModalOnlyVipTab}
          onClose={() => {
            setShareModalPropertyId(null);
            setShareModalVipDraft(null);
            setShareModalOnlyVipTab(false);
            setShareModalInitialTab('email');
            setTimeout(() => setShareStatsVersion((v) => v + 1), 600);
          }}
          onShared={() => {
            setShareStatsVersion((v) => v + 1);
            // Plusieurs rafraîchissements décalés : Firestore peut mettre un moment à propager sharedWithMe
            setTimeout(() => setShareStatsVersion((v) => v + 1), 800);
            setTimeout(() => setShareStatsVersion((v) => v + 1), 2000);
            setTimeout(() => setShareStatsVersion((v) => v + 1), 4000);
          }}
        />
      )}
      {shareWithBrokerPropertyId && (
        <ShareWithBrokerModal
          propertyId={shareWithBrokerPropertyId}
          selectedBroker={selectedBrokerForShare}
          alreadySharedWithThisBroker={(() => {
            const prop = properties?.find((p) => p.id === shareWithBrokerPropertyId);
            const broker = selectedBrokerForShare || shareEligibleBrokerLinks?.[0];
            if (!prop || !broker?.brokerUid) return false;
            return propertyHasActiveBrokerDashboardShare(prop, broker.brokerUid);
          })()}
          onClose={() => {
            setShareWithBrokerPropertyId(null);
            setSelectedBrokerForShare(null);
          }}
          onShared={(id) => {
            const broker = selectedBrokerForShare || shareEligibleBrokerLinks?.[0];
            setShareWithBrokerPropertyId(null);
            setSelectedBrokerForShare(null);
            setCurrentProperty((prev) => (prev?.id === id && broker ? { ...prev, brokerUid: broker.brokerUid, clientId: broker.clientId } : prev));
          }}
        />
      )}
        </>
      )}
      <Header
        onNavigateToHome={() => {
          exitSharedDealViewMarkSeen();
          setCurrentStep('home');
        }}
        onNavigateToClients={isCourtierHypo ? () => { exitSharedDealViewMarkSeen(); setCurrentStep('clients'); } : undefined}
        onNavigateToChecklists={isCourtierHypo ? () => { exitSharedDealViewMarkSeen(); setCurrentStep('checklists'); } : undefined}
        onNavigateToMarketParams={undefined}
        onNavigateToMyFiles={() => { exitSharedDealViewMarkSeen(); setCurrentStep('myFiles'); }}
      />
      <FloatingDirectChatDock
        open={directChatDockOpen}
        onOpenChange={setDirectChatDockOpen}
        currentStep={currentStep}
        currentProperty={currentProperty}
        clients={clients}
        onOpenDealFromChat={handleOpenDealFromChat}
      />
      {feedbackEnabled ? (
        <FloatingFeedbackDock
          open={feedbackDockOpen}
          onOpenChange={setFeedbackDockOpen}
          currentStep={currentStep}
          currentProperty={currentProperty}
        />
      ) : null}
      <FloatingOnboardingAssistant dockAboveFeedback={feedbackEnabled} />
      <DealPurposeModal
        open={dealPurposeModalOpen}
        onClose={() => setDealPurposeModalOpen(false)}
        onSelect={handleDealPurposeSelect}
      />
      <ListingImportModal
        open={listingImportOpen}
        onClose={handleListingImportClose}
        onApply={handleListingImportApply}
        t={t}
        language={language === 'en' ? 'en' : 'fr'}
      />
      {!viewingShareData && showShareAddedBanner && (
        <div className="bg-green-600 text-white text-center py-3 px-4 text-sm font-medium">
          {t('home.shareAddedBanner')}
        </div>
      )}
      {!viewingShareData && showPocketDealLinkEmailMismatchBanner && (
        <div className="bg-amber-50 border-b border-amber-200 text-amber-950 text-center py-3 px-4 text-sm">
          <p className="max-w-3xl mx-auto">{t('home.pocketDealLinkEmailMismatchBanner')}</p>
          <button
            type="button"
            className="mt-2 text-amber-900 underline text-sm font-medium hover:no-underline"
            onClick={() => setShowPocketDealLinkEmailMismatchBanner(false)}
          >
            {t('close')}
          </button>
        </div>
      )}
      {!viewingShareData && propertyToDelete && (
        <DeletePropertyConfirmModal
          property={propertyToDelete}
          onClose={() => setPropertyToDelete(null)}
          onConfirm={() => handleDeleteProperty(propertyToDelete)}
        />
      )}
      {!viewingShareData && propertyToDuplicate && (
        <DuplicatePropertyModal
          property={propertyToDuplicate}
          onClose={() => setPropertyToDuplicate(null)}
          onConfirm={handleDuplicateProperty}
        />
      )}
      {viewingShareData ? (
        <SharedPropertyView
          shareData={viewingShareData.shareData}
          openScenario={viewingShareData.openScenario}
          onBack={exitSharedDealViewMarkSeen}
          onRemoveFromSharedDashboard={
            viewingShareData.sharedDocId && currentUser?.uid ? removeSharedPropertyFromDashboard : undefined
          }
          onOpenDealFromChat={handleOpenDealFromChat}
        />
      ) : propertiesLoading ? (
        <div className="flex items-center justify-center py-10">
          <span className="text-gray-700">{t('loading')}</span>
        </div>
      ) : (
        <>
          {currentStep === 'home' && (
            <HomeScreen
              properties={properties}
              purchaseOffersModuleEnabled={userProfile?.modules?.purchaseOffersEnabled === true}
              sharedWithMe={sharedWithMe || []}
              shareStats={shareStats}
              shareStatsVersion={shareStatsVersion}
              onSelectSharedWithMe={async (docId, shareToken) => {
                const shareData = await getShare(shareToken);
                if (shareData) setViewingShareData({ shareData, sharedDocId: docId });
              }}
              onRemoveSharedWithMe={currentUser?.uid ? (docId, shareToken) => removeSharedWithMe(currentUser.uid, docId, shareToken) : undefined}
              onNew={resetProperty}
              onSelect={(property) => {
                setCurrentProperty(property);
                setAdvancedExpenses(property.advancedExpenses || false);
                setCurrentStep('dashboard');
              }}
              onDelete={(property) => setPropertyToDelete(property)}
              onDuplicate={(property) => setPropertyToDuplicate(property)}
              onShare={handleShare}
              onAbout={() => setCurrentStep('about')}
              currentUserId={currentUser?.uid}
              clients={clients}
              isBrokerView={isCourtierHypo}
              isPreteurPrive={userProfile?.persona === 'preteur_prive'}
              financedDealsAsOwner={financedDealsAsOwner || []}
              financedDealsAsLender={financedDealsAsLender || []}
              fundInvestorsAsInvestor={fundInvestorsAsInvestor || []}
              pendingLoanDetailDeal={pendingLoanDetailDeal}
              onConsumePendingLoanDetail={() => setPendingLoanDetailDeal(null)}
              initialActiveTab={postInviteFocus?.focusTab || null}
              openFundInvestorId={postInviteFocus?.openFundInvestorId || null}
              showPocketCampaignsTab={userProfile?.persona === 'courtier_immo'}
              onResubmitPocketCampaign={handleResubmitPocketCampaign}
              onDismissShareAddedBanner={() => setShowShareAddedBanner(false)}
            />
          )}
          {currentStep === 'form' && (
            <PropertyForm
              currentProperty={currentProperty}
              setCurrentProperty={setCurrentProperty}
              setCurrentStep={setCurrentStep}
              advancedExpenses={advancedExpenses}
              setAdvancedExpenses={setAdvancedExpenses}
              onSave={handleSave}
              marketingSectionScrollTrigger={marketingSectionScrollTrigger}
              userProfile={userProfile}
              onOpenListingImport={() => setListingImportOpen(true)}
              listingImportWarnings={listingImportWarnings}
            />
          )}
          {currentStep === 'dashboard' && (
            <BuildingDashboard
              property={currentProperty}
              onCreateScenario={() => {
                setCurrentScenario(null);
                setCurrentStep('scenario');
              }}
              onEditScenario={(sc) => {
                setCurrentScenario(sc);
                setCurrentStep('scenario');
              }}
              onViewScenario={(sc) => {
                setCurrentScenario(sc);
                setCurrentStep('report');
              }}
              onEditProperty={() => setCurrentStep('form')}
              onBack={() => setCurrentStep('home')}
              onOpenProjectDocuments={() => setCurrentStep('projectDocuments')}
              onSaveRentRegister={async (rentRegister) => {
                if (!currentProperty?.id || currentUser?.uid !== currentProperty?.uid) return;
                await updateProperty(currentProperty.id, { rentRegister });
                setCurrentProperty((prev) => ({ ...prev, rentRegister }));
              }}
              onOpenFinancementDossier={(dossier) => {
                setSelectedFinancementDossier(dossier);
                setCurrentStep('financementDossier');
              }}
              onOpenLoanDetail={(deal) => {
                setPendingLoanDetailDeal(deal);
                setCurrentStep('home');
              }}
              clients={clients}
              isCourtierHypo={isCourtierHypo}
              isPreteurPrive={userProfile?.persona === 'preteur_prive'}
              additionalScenariosFromShares={sharedScenariosFromClients}
              financedDealsAsLender={financedDealsAsLender || []}
              financedDealsAsOwner={financedDealsAsOwner || []}
              onOpenDealFromChat={handleOpenDealFromChat}
            />
          )}
          {currentStep === 'projectDocuments' && (
            <ProjectDocumentsPage
              property={currentProperty}
              onBack={() => setCurrentStep('dashboard')}
            />
          )}
          {currentStep === 'financementDossier' && selectedFinancementDossier && currentProperty && (
            <FinancementDossierPage
              dossier={selectedFinancementDossier}
              property={currentProperty}
              onBack={() => { setSelectedFinancementDossier(null); setCurrentStep('dashboard'); }}
            />
          )}
          {currentStep === 'scenario' && (
            <FinancingScenarioForm
              propertyId={currentProperty.id}
              property={currentProperty}
              advancedExpenses={advancedExpenses}
              initialScenario={currentScenario || {}}
              creatorUid={currentUser?.uid}
              onSaved={(sc) => {
                setCurrentScenario(sc);
                setCurrentStep('report');
              }}
              onBack={() => setCurrentStep('dashboard')}
            />
          )}
          {currentStep === 'report' && (
            <PropertyReport
              currentProperty={currentProperty}
              setCurrentStep={setCurrentStep}
              analysis={analysis}
              onSave={handleSave}
              advancedExpenses={advancedExpenses}
              scenario={currentScenario}
              onViewAmortization={handleViewAmortization}
              sharedScenariosFromClients={sharedScenariosFromClients}
            />
          )}
          {currentStep === 'amortization' && (
            <AmortizationPage
              analysis={amortizationData?.analysis || analysis}
              currentProperty={amortizationData?.property || currentProperty}
              scenario={amortizationData?.scenario}
              scenarioAnalysis={amortizationData?.scenarioAnalysis}
              setCurrentStep={setCurrentStep}
            />
          )}
          {currentStep === 'myFiles' && (
            <MyFilesPage onBack={() => setCurrentStep('home')} />
          )}
          {currentStep === 'about' && (
            <AboutPage onBack={() => setCurrentStep('home')} />
          )}
          {currentStep === 'clients' && (
            <ClientsPage
              onBack={() => setCurrentStep('home')}
              isPrivateLender={userProfile?.persona === 'preteur_prive'}
              isRealEstateBroker={userProfile?.persona === 'courtier_immo'}
            />
          )}
          {currentStep === 'checklists' && (
            <ChecklistsPage onBack={() => setCurrentStep('home')} />
          )}
          {currentStep === 'marketParams' && (
            <MarketParamsPage onBack={() => setCurrentStep('home')} />
          )}
        </>
      )}
    </div>
    </OnboardingProvider>
  );
};

export default RentalPropertyAnalyzer;
